import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import styled from 'styled-components';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { IOT_METRICS, IOT_METRIC_KEYS, METRIC_COLORS, formatKstDateTime, formatMetricValue } from './iot_metrics';
import { CONNECTION_TONES, MQTT_TONES, iotColor, iotFont, iotLayout, iotMixin, iotRadius } from './iot_console_theme';

// 장치 목록과 MQTT 브로커 상태를 30초마다 함께 다시 읽는다. 검색·상태 필터·정렬·표시 항목은 화면에서만 적용한다.
const CONNECTION_LABELS = Object.freeze(['온라인', '오프라인', '연결 대기', '사용중지']);
const STATUS_FILTERS = Object.freeze(['전체', ...CONNECTION_LABELS]);
const REFRESH_MS = 30000;
const PAGE_SIZE = 12;
const VISIBLE_METRICS_STORAGE_KEY = 'iot.devices.visibleMetrics';
// HCHO(ch2o)는 프로토콜 정의 항목이지만 STG 실모듈 11대 중 보내는 기종이 없다(2026-09-24 실측) — 기본은 숨기고 표시 항목에서 켤 수 있다.
const DEFAULT_VISIBLE_METRIC_KEYS = Object.freeze(IOT_METRIC_KEYS.filter((key) => key !== 'ch2o'));
const SORT_OPTIONS = Object.freeze([
    { value: 'recent', label: '최근 수신순' },
    { value: 'name', label: '장치명순' },
    { value: 'pm2_5', label: 'PM2.5 높은순' }
]);
// 고정 5열(상태·장치·위치·등록자·최근 수신) 최소 폭 합과 센서 열 최소 폭 — 좁은 화면은 카드 안에서 가로 스크롤한다.
const FIXED_COLUMNS_MIN_WIDTH = 640;
const METRIC_COLUMN_MIN_WIDTH = 62;
const KST_PARTS_FORMATTER = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Seoul',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23'
});

function connectionLabel(device) {
    if (device.connection_state === 'NEVER_CONNECTED') return '연결 대기';
    if (device.connection_state === 'DISABLED' || device.status === 'DISABLED') return '사용중지';
    return device.connection_state === 'ONLINE' || device.online ? '온라인' : '오프라인';
}

function kstParts(time) {
    return Object.fromEntries(
        KST_PARTS_FORMATTER.formatToParts(time)
            .filter((part) => part.type !== 'literal')
            .map((part) => [part.type, part.value])
    );
}

// KST 기준 오늘 수신이면 `HH:mm`, 그 외에는 `MM.DD HH:mm`.
function compactKstDateTime(value) {
    if (!value) return '-';
    const parsed = Date.parse(value);
    if (!Number.isFinite(parsed)) return String(value);
    const parts = kstParts(parsed);
    const today = kstParts(Date.now());
    const time = `${parts.hour}:${parts.minute}`;
    const isToday = parts.year === today.year && parts.month === today.month && parts.day === today.day;
    return isToday ? time : `${parts.month}.${parts.day} ${time}`;
}

function countByConnection(devices) {
    return devices.reduce((counts, device) => {
        const label = connectionLabel(device);
        return { ...counts, [label]: (counts[label] || 0) + 1 };
    }, {});
}

function deviceName(device) {
    return device.display_name || device.name || device.device_id;
}

function metricHeading(metric) {
    return metric.key === 'ch2o' ? 'HCHO' : metric.label;
}

function metricNumber(device, key) {
    const value = device.last_metrics?.[key];
    if (value === null || value === undefined || value === '') return null;
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : null;
}

function timeValue(value) {
    const parsed = Date.parse(value || '');
    return Number.isFinite(parsed) ? parsed : null;
}

// 내림차순, 값이 없는 항목은 뒤로.
function compareDescending(left, right) {
    if (left === null && right === null) return 0;
    if (left === null) return 1;
    if (right === null) return -1;
    return right - left;
}

const SORTERS = Object.freeze({
    recent: (a, b) => compareDescending(timeValue(a.last_seen_at), timeValue(b.last_seen_at)),
    name: (a, b) => deviceName(a).localeCompare(deviceName(b), 'ko'),
    pm2_5: (a, b) => compareDescending(metricNumber(a, 'pm2_5'), metricNumber(b, 'pm2_5'))
});

function matchesSearch(device, term) {
    if (!term) return true;
    return [device.display_name, device.name, device.device_id, device.owner?.user_name, device.owner?.email, device.location]
        .some((value) => typeof value === 'string' && value.toLowerCase().includes(term));
}

// 미니 막대 기준 범위 — 로드된 전체 장치의 센서별 최소·최대.
function metricRanges(devices) {
    return Object.fromEntries(IOT_METRIC_KEYS.map((key) => {
        const values = devices.map((device) => metricNumber(device, key)).filter((value) => value !== null);
        return [key, values.length ? { min: Math.min(...values), max: Math.max(...values) } : null];
    }));
}

function barWidth(value, range) {
    if (value === null || !range) return null;
    const ratio = range.max === range.min ? 1 : (value - range.min) / (range.max - range.min);
    return `${(8 + ratio * 92).toFixed(0)}%`;
}

function readVisibleMetrics() {
    try {
        const stored = JSON.parse(window.localStorage.getItem(VISIBLE_METRICS_STORAGE_KEY));
        if (Array.isArray(stored)) {
            const keys = IOT_METRIC_KEYS.filter((key) => stored.includes(key));
            if (keys.length > 0) return keys;
        }
    } catch {
        // 저장소를 쓸 수 없는 환경은 기본 표시 항목을 쓴다.
    }
    return [...DEFAULT_VISIBLE_METRIC_KEYS];
}

function writeVisibleMetrics(keys) {
    try {
        window.localStorage.setItem(VISIBLE_METRICS_STORAGE_KEY, JSON.stringify(keys));
    } catch {
        // 저장 실패 시 현재 화면에만 반영한다.
    }
}

function IotDevices() {
    const { role } = useOutletContext() || {};
    const canView = role === 'admin' || role === 'staff';
    const navigate = useNavigate();
    const [devices, setDevices] = useState([]);
    const [loaded, setLoaded] = useState(false);
    const [error, setError] = useState('');
    const [mqttStatus, setMqttStatus] = useState('CHECKING');
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('전체');
    const [sort, setSort] = useState('recent');
    const [limit, setLimit] = useState(PAGE_SIZE);
    const [visibleMetricKeys, setVisibleMetricKeys] = useState(readVisibleMetrics);
    const [metricsMenuOpen, setMetricsMenuOpen] = useState(false);
    const metricsMenuRef = useRef(null);
    const metricsButtonRef = useRef(null);

    // 재조회 실패 시에도 기존 목록은 유지하고 에러만 표시한다.
    const loadDevices = async () => {
        try {
            const response = await axios.get('/api/admin/iot/devices');
            setDevices(response.data.devices || []);
            setError('');
        } catch (loadError) {
            setError(loadError.response?.data?.message || 'IoT 장치 목록을 불러오지 못했습니다.');
        } finally {
            setLoaded(true);
        }
    };

    const loadMqttStatus = async () => {
        try {
            const response = await axios.get('/api/admin/iot/status');
            setMqttStatus(response.data.mqtt?.operational === true ? 'RUNNING' : 'PROBLEM');
        } catch {
            setMqttStatus('PROBLEM');
        }
    };

    const refresh = () => {
        loadDevices();
        loadMqttStatus();
    };

    useEffect(() => {
        if (!canView) return undefined;
        refresh();
        const refreshTimer = setInterval(refresh, REFRESH_MS);
        return () => clearInterval(refreshTimer);
        // 장치 목록·MQTT 상태는 첫 진입 후 30초마다 함께 다시 읽는다.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [canView]);

    useEffect(() => {
        if (!metricsMenuOpen) return undefined;
        const closeOnOutside = (event) => {
            if (!metricsMenuRef.current?.contains(event.target)) setMetricsMenuOpen(false);
        };
        const closeOnEscape = (event) => {
            if (event.key !== 'Escape') return;
            setMetricsMenuOpen(false);
            metricsButtonRef.current?.focus();
        };
        document.addEventListener('mousedown', closeOnOutside);
        document.addEventListener('keydown', closeOnEscape);
        return () => {
            document.removeEventListener('mousedown', closeOnOutside);
            document.removeEventListener('keydown', closeOnEscape);
        };
    }, [metricsMenuOpen]);

    if (!canView) {
        return (
            <PageShell>
                <StatePanel role="alert">IoT 모니터링 권한이 필요합니다.</StatePanel>
            </PageShell>
        );
    }

    const mqttLabel = mqttStatus === 'RUNNING'
        ? 'MQTT 서버 정상'
        : (mqttStatus === 'CHECKING' ? 'MQTT 확인 중' : 'MQTT 서버 문제');
    const connectionCounts = countByConnection(devices);
    const searchTerm = search.trim().toLowerCase();
    const filteredDevices = devices
        .filter((device) => (statusFilter === '전체' || connectionLabel(device) === statusFilter) && matchesSearch(device, searchTerm))
        .sort(SORTERS[sort]);
    const shownDevices = filteredDevices.slice(0, limit);
    const visibleMetrics = IOT_METRICS.filter((metric) => visibleMetricKeys.includes(metric.key));
    const ranges = metricRanges(devices);
    const columns = `94px minmax(190px, 1.35fr) 104px 148px 104px repeat(${visibleMetrics.length}, minmax(62px, 0.82fr))`;
    const gridMinWidth = FIXED_COLUMNS_MIN_WIDTH + METRIC_COLUMN_MIN_WIDTH * visibleMetrics.length;

    const openDevice = (deviceId) => navigate('/iot_device_detail', { state: { deviceId } });
    const changeSearch = (value) => { setSearch(value); setLimit(PAGE_SIZE); };
    const changeStatusFilter = (value) => { setStatusFilter(value); setLimit(PAGE_SIZE); };
    const changeSort = (value) => { setSort(value); setLimit(PAGE_SIZE); };
    const toggleMetric = (key) => {
        const next = visibleMetricKeys.includes(key)
            ? visibleMetricKeys.filter((item) => item !== key)
            : IOT_METRIC_KEYS.filter((item) => item === key || visibleMetricKeys.includes(item));
        if (next.length === 0) return;
        setVisibleMetricKeys(next);
        writeVisibleMetrics(next);
    };

    return (
        <PageShell>
            <Content>
                <Header>
                    <div>
                        <PageTitle>IoT 모니터링</PageTitle>
                        <PageDescription>장치의 연결 상태와 최근 측정값, 등록 고객과 공유 정보를 확인하세요.</PageDescription>
                    </div>
                    <HeaderActions>
                        <MqttPill
                            $tone={MQTT_TONES[mqttStatus]}
                            role="status"
                            aria-live="polite"
                            title="MQTT 브로커 수신 상태 · 30초마다 확인"
                        >
                            <i aria-hidden="true" />{mqttLabel}
                        </MqttPill>
                        <RefreshButton type="button" onClick={refresh} title="장치 목록과 MQTT 상태를 지금 다시 불러옵니다">
                            <svg width="13" height="13" viewBox="0 0 16 16" fill="none" strokeWidth="1.6" strokeLinecap="round" aria-hidden="true">
                                <path d="M14 8a6 6 0 1 1-1.8-4.3" />
                                <path d="M14 2v3.5h-3.5" />
                            </svg>
                            30초마다 자동 갱신
                        </RefreshButton>
                    </HeaderActions>
                </Header>

                {error && <Message role="alert">{error}</Message>}
                {!loaded ? (
                    <StatePanel>IoT 장치 목록을 불러오는 중입니다.</StatePanel>
                ) : devices.length === 0 ? (
                    <StatePanel>등록된 IoT 장치가 없습니다. 첫 유효 MQTT telemetry 수신 시 자동 등록됩니다.</StatePanel>
                ) : (
                    <>
                        <Toolbar aria-label="장치 목록 조건">
                            <SearchField>
                                <svg width="15" height="15" viewBox="0 0 16 16" fill="none" strokeWidth="1.7" strokeLinecap="round" aria-hidden="true">
                                    <circle cx="7" cy="7" r="4.6" />
                                    <path d="M10.5 10.5 14 14" />
                                </svg>
                                <input
                                    type="search"
                                    value={search}
                                    onChange={(event) => changeSearch(event.target.value)}
                                    placeholder="장치명 · ID · 등록자 · 위치 검색"
                                    aria-label="장치 검색"
                                />
                            </SearchField>
                            <ChipGroup role="group" aria-label="연결 상태 필터">
                                {STATUS_FILTERS.map((label) => {
                                    const active = statusFilter === label;
                                    const count = label === '전체' ? devices.length : (connectionCounts[label] || 0);
                                    return (
                                        <FilterChip
                                            key={label}
                                            type="button"
                                            aria-pressed={active}
                                            $active={active}
                                            $dot={label === '전체' ? iotColor.textMuted : CONNECTION_TONES[label].dot}
                                            onClick={() => changeStatusFilter(label)}
                                        >
                                            <i aria-hidden="true" />{label}
                                            <ChipCount $active={active}>{count}</ChipCount>
                                        </FilterChip>
                                    );
                                })}
                            </ChipGroup>
                            <ToolbarEnd>
                                <SortSelect aria-label="정렬" value={sort} onChange={(event) => changeSort(event.target.value)}>
                                    {SORT_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                                </SortSelect>
                                <MetricsMenu ref={metricsMenuRef}>
                                    <ToolButton
                                        ref={metricsButtonRef}
                                        type="button"
                                        aria-expanded={metricsMenuOpen}
                                        aria-controls="iot-visible-metrics"
                                        onClick={() => setMetricsMenuOpen((open) => !open)}
                                    >
                                        <svg width="13" height="13" viewBox="0 0 16 16" fill="none" strokeWidth="1.7" strokeLinecap="round" aria-hidden="true">
                                            <path d="M2 4h12M4.5 8h7M7 12h2" />
                                        </svg>
                                        표시 항목
                                    </ToolButton>
                                    {metricsMenuOpen && (
                                        <MetricsPopover id="iot-visible-metrics" role="group" aria-label="표시할 센서">
                                            {IOT_METRICS.map((metric) => {
                                                const checked = visibleMetricKeys.includes(metric.key);
                                                return (
                                                    <MetricOption key={metric.key} $color={METRIC_COLORS[metric.key]}>
                                                        <input
                                                            type="checkbox"
                                                            checked={checked}
                                                            disabled={checked && visibleMetricKeys.length === 1}
                                                            onChange={() => toggleMetric(metric.key)}
                                                        />
                                                        <i aria-hidden="true" />{metricHeading(metric)}
                                                    </MetricOption>
                                                );
                                            })}
                                        </MetricsPopover>
                                    )}
                                </MetricsMenu>
                            </ToolbarEnd>
                        </Toolbar>

                        <ListCard aria-label="IoT 장치 목록">
                            <ListCardHeader>
                                <ListTitle>
                                    <h2>장치 목록</h2>
                                    <span>{`${filteredDevices.length}대`}</span>
                                </ListTitle>
                                <Legend>
                                    <LegendLabel>센서 최근값</LegendLabel>
                                    <LegendItems>
                                        {visibleMetrics.map((metric) => (
                                            <LegendItem key={metric.key} $color={METRIC_COLORS[metric.key]}>
                                                <i aria-hidden="true" />{metricHeading(metric)}
                                            </LegendItem>
                                        ))}
                                    </LegendItems>
                                </Legend>
                            </ListCardHeader>

                            {filteredDevices.length === 0 ? (
                                <EmptyList>조건에 맞는 장치가 없습니다.</EmptyList>
                            ) : (
                                <>
                                    <ListScroller>
                                        <div style={{ minWidth: `${gridMinWidth}px` }}>
                                            <ListHeader $columns={columns} aria-hidden="true">
                                                <span>상태</span>
                                                <span>장치</span>
                                                <span>위치</span>
                                                <span>등록자 · 공유</span>
                                                <span>최근 수신</span>
                                                {visibleMetrics.map((metric) => (
                                                    <MetricHeading key={metric.key} title={metric.label}>{metricHeading(metric)}</MetricHeading>
                                                ))}
                                            </ListHeader>
                                            {shownDevices.map((device) => {
                                                const state = connectionLabel(device);
                                                const online = state === '온라인';
                                                const name = deviceName(device);
                                                const sharedCount = device.shared_member_count ? `${device.shared_member_count}명` : '-';
                                                const ownerName = device.owner?.user_name || device.owner?.email || '소유자 없음';
                                                const ownerDetails = device.owner
                                                    ? [...new Set([device.owner.user_name, device.owner.email, device.owner.phone_num].filter(Boolean))].join(' · ')
                                                    : '소유자 없음';
                                                const registeredAt = formatKstDateTime(device.provisioned_at || device.created_at);
                                                const lastSeenAt = formatKstDateTime(device.last_seen_at);
                                                return (
                                                    <DeviceRow
                                                        key={device.device_id}
                                                        role="button"
                                                        tabIndex={0}
                                                        $columns={columns}
                                                        $disabled={state === '사용중지'}
                                                        aria-label={`${name} · ID ${device.device_id} 상세 보기`}
                                                        onClick={() => openDevice(device.device_id)}
                                                        onKeyDown={(event) => {
                                                            if (event.key !== 'Enter' && event.key !== ' ') return;
                                                            event.preventDefault();
                                                            openDevice(device.device_id);
                                                        }}
                                                    >
                                                        <Cell>
                                                            <StatusChip $tone={CONNECTION_TONES[state]}><i aria-hidden="true" />{state}</StatusChip>
                                                        </Cell>
                                                        <IdentityCell title={`${name} · ID ${device.device_id}`}>
                                                            <DeviceName>{name}</DeviceName>
                                                            <DeviceId>ID {device.device_id}</DeviceId>
                                                        </IdentityCell>
                                                        <Cell title={device.location || '-'}>
                                                            <CellText>{device.location || '-'}</CellText>
                                                        </Cell>
                                                        <OwnerCell title={`등록자 ${ownerDetails} · 공유 ${sharedCount}`}>
                                                            <CellText>{ownerName}</CellText>
                                                            {device.owner && (
                                                                <ShareBadge>{device.shared_member_count > 0 ? `${device.shared_member_count}명` : '단독'}</ShareBadge>
                                                            )}
                                                        </OwnerCell>
                                                        <Cell title={`등록 ${registeredAt} · 최근 수신 ${lastSeenAt}`}>
                                                            <SeenText $online={online}>{compactKstDateTime(device.last_seen_at)}</SeenText>
                                                        </Cell>
                                                        {visibleMetrics.map((metric) => {
                                                            const value = formatMetricValue(metric.key, device.last_metrics?.[metric.key]);
                                                            const width = barWidth(metricNumber(device, metric.key), ranges[metric.key]);
                                                            const description = `${metric.label} ${value}${value !== '-' ? ` ${metric.unit}` : ''}`;
                                                            return (
                                                                <MetricCell key={metric.key} aria-label={description} title={description}>
                                                                    <MetricValue $online={online}>{value === '-' ? '—' : value}</MetricValue>
                                                                    <MetricBar aria-hidden="true">
                                                                        {width && <span style={{ width, background: METRIC_COLORS[metric.key] }} />}
                                                                    </MetricBar>
                                                                </MetricCell>
                                                            );
                                                        })}
                                                    </DeviceRow>
                                                );
                                            })}
                                        </div>
                                    </ListScroller>
                                    <ListFooter>
                                        <span>{`${filteredDevices.length}대 중 ${shownDevices.length}대 표시`}</span>
                                        {shownDevices.length < filteredDevices.length && (
                                            <MoreButton type="button" onClick={() => setLimit((current) => current + PAGE_SIZE)}>더 보기</MoreButton>
                                        )}
                                    </ListFooter>
                                </>
                            )}
                        </ListCard>
                    </>
                )}
            </Content>
        </PageShell>
    );
}

export default IotDevices;

const PageShell = styled.main`
    min-height: calc(100vh - 130px);
    padding: ${iotLayout.pagePadding};
    background: ${iotColor.page};
    color: ${iotColor.text};

    @media (max-width: 720px) {
        padding: ${iotLayout.pagePaddingSmall};
    }
`;

const Content = styled.div`
    max-width: ${iotLayout.maxWidth};
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    gap: ${iotLayout.gap};
`;

const Header = styled.header`
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 24px;

    @media (max-width: 820px) {
        align-items: stretch;
        flex-direction: column;
        gap: 12px;
    }
`;

const PageTitle = styled.h1`
    margin: 0;
    color: ${iotColor.ink};
    font-size: 24px;
    font-weight: 800;
    line-height: 1.25;
    letter-spacing: -0.02em;
`;

const PageDescription = styled.p`
    margin: 6px 0 0;
    color: ${iotColor.textMuted};
    font-size: 13px;
    line-height: 1.5;
`;

const HeaderActions = styled.div`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
`;

const MqttPill = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 7px;
    height: 34px;
    padding: 0 13px;
    border: 1px solid ${({ $tone }) => $tone.border};
    border-radius: ${iotRadius.pill};
    background: ${({ $tone }) => $tone.bg};
    color: ${({ $tone }) => $tone.fg};
    font-size: 12px;
    font-weight: 700;
    white-space: nowrap;

    i {
        display: block;
        width: 7px;
        height: 7px;
        border-radius: 50%;
        background: ${({ $tone }) => $tone.dot};
        box-shadow: 0 0 0 3px ${({ $tone }) => $tone.ring};
    }
`;

const ToolButton = styled.button`
    ${iotMixin.control};
    ${iotMixin.focusRing};
    display: inline-flex;
    align-items: center;
    gap: 7px;
    padding: 0 14px;
    font-weight: 700;
    white-space: nowrap;
    cursor: pointer;

    svg {
        flex: 0 0 auto;
        stroke: ${iotColor.textMuted};
    }

    &:hover {
        background: ${iotColor.rowHover};
    }
`;

const RefreshButton = styled(ToolButton)`
    height: 34px;
    font-size: 12px;
`;

const Toolbar = styled.section`
    ${iotMixin.card};
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px;
    padding: 14px 16px;
`;

const SearchField = styled.label`
    position: relative;
    display: flex;
    align-items: center;
    flex: 1 1 300px;
    min-width: min(260px, 100%);

    svg {
        position: absolute;
        left: 12px;
        stroke: ${iotColor.textMuted};
        pointer-events: none;
    }

    input {
        ${iotMixin.control};
        ${iotMixin.focusRing};
        width: 100%;
        padding: 0 12px 0 34px;
        color: ${iotColor.ink};
        font-size: 13px;
    }
`;

const ChipGroup = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
`;

const FilterChip = styled.button`
    ${iotMixin.focusRing};
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 38px;
    padding: 0 13px;
    border: 1px solid ${({ $active }) => $active ? iotColor.chipActive : iotColor.controlLine};
    border-radius: ${iotRadius.control};
    background: ${({ $active }) => $active ? iotColor.chipActive : iotColor.surface};
    color: ${({ $active }) => $active ? iotColor.onDark : iotColor.textSub};
    font-family: inherit;
    font-size: 12.5px;
    font-weight: 700;
    white-space: nowrap;
    cursor: pointer;

    i {
        display: block;
        width: 7px;
        height: 7px;
        border-radius: 50%;
        background: ${({ $dot }) => $dot};
    }
`;

const ChipCount = styled.span`
    color: ${({ $active }) => $active ? iotColor.chipActiveCount : iotColor.textMuted};
    font-variant-numeric: tabular-nums;
`;

const ToolbarEnd = styled.div`
    margin-left: auto;
    display: flex;
    align-items: center;
    gap: 8px;
`;

const SortSelect = styled.select`
    ${iotMixin.control};
    ${iotMixin.focusRing};
    padding: 0 30px 0 12px;
    font-weight: 600;
    cursor: pointer;
`;

const MetricsMenu = styled.div`
    position: relative;
`;

const MetricsPopover = styled.div`
    ${iotMixin.popover};
    position: absolute;
    top: calc(100% + 6px);
    right: 0;
    z-index: 10;
    min-width: 176px;
    padding: 6px;
    display: flex;
    flex-direction: column;
`;

const MetricOption = styled.label`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 7px 8px;
    border-radius: ${iotRadius.tag};
    color: ${iotColor.text};
    font-size: 12.5px;
    font-weight: 600;
    cursor: pointer;

    &:hover {
        background: ${iotColor.rowHover};
    }

    input {
        margin: 0;
        accent-color: ${iotColor.accent};
    }

    i {
        display: block;
        width: 7px;
        height: 7px;
        border-radius: 50%;
        background: ${({ $color }) => $color};
    }
`;

const ListCard = styled.section`
    ${iotMixin.card};
    overflow: hidden;
`;

const ListCardHeader = styled.header`
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 16px;
    padding: 14px 18px;
    border-bottom: 1px solid ${iotColor.cardHeaderLine};
`;

const ListTitle = styled.div`
    display: flex;
    align-items: baseline;
    gap: 8px;

    h2 {
        margin: 0;
        color: ${iotColor.ink};
        font-size: 15px;
        font-weight: 700;
    }

    span {
        color: ${iotColor.textMuted};
        font-size: 12.5px;
        font-weight: 600;
        font-variant-numeric: tabular-nums;
    }
`;

const Legend = styled.div`
    display: flex;
    align-items: center;
    gap: 14px;
`;

const LegendLabel = styled.span`
    color: ${iotColor.textMuted};
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.04em;
    white-space: nowrap;
`;

const LegendItems = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 11px;
`;

const LegendItem = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 5px;
    color: ${iotColor.textMuted};
    font-size: 11.5px;
    font-weight: 600;

    i {
        display: block;
        width: 7px;
        height: 7px;
        border-radius: 50%;
        background: ${({ $color }) => $color};
    }
`;

const ListScroller = styled.div`
    overflow-x: auto;
`;

const ListHeader = styled.div`
    display: grid;
    grid-template-columns: ${({ $columns }) => $columns};
    background: ${iotColor.headerRow};
    border-bottom: 1px solid ${iotColor.cardLine};
    color: ${iotColor.textMuted};
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.04em;

    > span {
        min-width: 0;
        padding: 9px 12px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }
`;

const MetricHeading = styled.span`
    && {
        padding: 9px 10px;
        text-align: right;
    }
`;

// role="button"은 article에 허용되지 않아(axe aria-allowed-role) 목록 행은 div로 둔다.
const DeviceRow = styled.div`
    display: grid;
    grid-template-columns: ${({ $columns }) => $columns};
    align-items: center;
    min-height: 56px;
    border-bottom: 1px solid ${iotColor.rowLine};
    background: ${({ $disabled }) => $disabled ? iotColor.disabledRow : iotColor.surface};
    cursor: pointer;
    transition: background-color 0.15s;

    &:hover {
        background: ${iotColor.rowHover};
    }

    &:focus-visible {
        position: relative;
        z-index: 1;
        outline: 2px solid ${iotColor.accent};
        outline-offset: -2px;
    }
`;

const Cell = styled.div`
    min-width: 0;
    padding: 0 12px;
`;

const StatusChip = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 9px;
    border: 1px solid ${({ $tone }) => $tone.border};
    border-radius: ${iotRadius.pill};
    background: ${({ $tone }) => $tone.bg};
    color: ${({ $tone }) => $tone.fg};
    font-size: 11.5px;
    font-weight: 700;
    white-space: nowrap;

    i {
        display: block;
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background: ${({ $tone }) => $tone.dot};
    }
`;

const IdentityCell = styled(Cell)`
    display: flex;
    flex-direction: column;
    gap: 2px;
`;

const ellipsis = `
    display: block;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const DeviceName = styled.strong`
    ${ellipsis};
    color: ${iotColor.ink};
    font-size: 13.5px;
    font-weight: 700;
    line-height: 1.25;
    letter-spacing: -0.01em;
`;

const DeviceId = styled.span`
    ${iotFont.mono};
    ${ellipsis};
    color: ${iotColor.textMuted};
    font-size: 11px;
    font-weight: 500;
    line-height: 1.2;
`;

const CellText = styled.span`
    ${ellipsis};
    color: ${iotColor.textSub};
    font-size: 12.5px;
    font-weight: 600;
`;

const OwnerCell = styled(Cell)`
    display: flex;
    align-items: center;
    gap: 6px;
`;

const ShareBadge = styled.span`
    flex: 0 0 auto;
    padding: 2px 6px;
    border-radius: ${iotRadius.badge};
    background: ${iotColor.rowLine};
    color: ${iotColor.textMuted};
    font-size: 10.5px;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
`;

const SeenText = styled.span`
    display: block;
    color: ${({ $online }) => $online ? iotColor.textSub : iotColor.textMuted};
    font-size: 12px;
    font-weight: 600;
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
`;

const MetricCell = styled.div`
    min-width: 0;
    padding: 0 10px;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 5px;
`;

const MetricValue = styled.strong`
    color: ${({ $online }) => $online ? iotColor.ink : iotColor.textMuted};
    font-size: 12.5px;
    font-weight: 700;
    line-height: 1;
    font-variant-numeric: tabular-nums;
`;

const MetricBar = styled.span`
    display: block;
    width: 100%;
    height: 3px;
    overflow: hidden;
    border-radius: ${iotRadius.pill};
    background: ${iotColor.rowLine};

    span {
        display: block;
        height: 3px;
        border-radius: ${iotRadius.pill};
    }
`;

const ListFooter = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 14px 18px;
    color: ${iotColor.textMuted};
    font-size: 12px;
    font-weight: 600;
`;

const MoreButton = styled(ToolButton)`
    height: 36px;
    padding: 0 18px;
`;

const EmptyList = styled.p`
    margin: 0;
    padding: 40px 18px;
    color: ${iotColor.textMuted};
    font-size: 13px;
    text-align: center;
`;

const Message = styled.div`
    padding: 12px 16px;
    border: 1px solid ${CONNECTION_TONES.오프라인.border};
    border-radius: ${iotRadius.control};
    background: ${CONNECTION_TONES.오프라인.bg};
    color: ${CONNECTION_TONES.오프라인.fg};
    font-size: 13px;
    font-weight: 600;
`;

const StatePanel = styled.div`
    ${iotMixin.card};
    max-width: ${iotLayout.maxWidth};
    margin: 0 auto;
    padding: 32px;
    color: ${iotColor.textMuted};
    font-size: 14px;
    text-align: center;
`;
